import { type FC } from "react";

import type { Size } from "@/assets/icons/utils";
import { CLionLogo } from "@/assets/logos/CLionLogo";
import { CursorLogo } from "@/assets/logos/CursorLogo";
import { GoLandLogo } from "@/assets/logos/GoLandLogo";
import { IntelliJLogo } from "@/assets/logos/IntelliJLogo";
import { PhpStormLogo } from "@/assets/logos/PhpStormLogo";
import { PyCharmLogo } from "@/assets/logos/PyCharmLogo";
import { RiderLogo } from "@/assets/logos/RiderLogo";
import { RubyMineLogo } from "@/assets/logos/RubyMineLogo";
import { RustRoverLogo } from "@/assets/logos/RustRoverLogo";
import { VSCodeInsidersLogo } from "@/assets/logos/VSCodeInsidersLogo";
import { VSCodeLogo } from "@/assets/logos/VSCodeLogo";
import { WebStormLogo } from "@/assets/logos/WebStormLogo";
import { WindsurfLogo } from "@/assets/logos/WindsurfLogo";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import type { PlainEditor } from "@/queries/editor-queries";

/**
 * A component that renders an editor icon.
 *
 * For avoiding redundancy, sets the `aria-hidden` attribute so its invisible to the accessibility API.
 * Because of that, users of this component should also render a label next to the icon (even with `sr-only`).
 */
export const EditorIcon: FC<{ editor: PlainEditor; size: Size } & PropsWithClassName> = ({
    editor,
    size,
    className,
}) => {
    let ComputedEditorLogo: React.ComponentType<PropsWithClassName>;
    switch (editor.alias) {
        case "vscode":
            ComputedEditorLogo = VSCodeLogo;
            break;
        case "vscode-insiders":
            ComputedEditorLogo = VSCodeInsidersLogo;
            break;
        case "cursor":
            ComputedEditorLogo = CursorLogo;
            break;
        case "windsurf":
            ComputedEditorLogo = WindsurfLogo;
            break;
        case "idea":
            ComputedEditorLogo = IntelliJLogo;
            break;
        case "goland":
            ComputedEditorLogo = GoLandLogo;
            break;
        case "pycharm":
            ComputedEditorLogo = PyCharmLogo;
            break;
        case "phpstorm":
            ComputedEditorLogo = PhpStormLogo;
            break;
        case "rubymine":
            ComputedEditorLogo = RubyMineLogo;
            break;
        case "webstorm":
            ComputedEditorLogo = WebStormLogo;
            break;
        case "clion":
            ComputedEditorLogo = CLionLogo;
            break;
        case "rustrover":
            ComputedEditorLogo = RustRoverLogo;
            break;
        case "rider":
            ComputedEditorLogo = RiderLogo;
            break;
        default:
            ComputedEditorLogo = VSCodeLogo;
    }

    return (
        <span aria-hidden>
            <ComputedEditorLogo
                className={cn(
                    className,
                    size === "sm" && "size-[16px]",
                    size === "base" && "size-[20px]",
                    size === "lg" && "size-[24px]",
                )}
            />
        </span>
    );
};
